/**
 * Estado de Cuenta Cliente x Mayor — migración web de ares9730.
 *
 * Estado de cuenta de un cliente del módulo Ventas x Mayor a una fecha
 * de corte: facturas a crédito con balance, antigüedad de saldo y el
 * detalle de cuotas de cada factura (valor, pagado, pendiente). Exporta a
 * Excel e imprime el estado de cuenta completo con los datos de la
 * compañía.
 *
 * Los tramos son DÍAS VENCIDOS de cada cuota (no antigüedad de la
 * factura) y muestran SOLO la mora: lo que aún no vence queda dentro del
 * BALANCE pero fuera de los tramos (el backend lo devuelve en tporvencer).
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { hayOtraMoneda, monedaLabel, normalizaLegacy } from "lib/moneda";
import { etiquetaCuota } from "modules/ventasxmayor/impresionxmayor";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

/**
 * Total del reporte. `lineas` reemplaza al valor único cuando el reporte trae
 * facturas fuera de la moneda base: una línea por moneda, porque sumarlas en
 * una sola cifra no dice cuánto hay que cobrar en cada una. `nota` lleva el
 * equivalente convertido, que es el que cuadra con el resto del documento.
 */
function ResumenChip({ label, value, lineas, nota, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      {lineas?.length ? (
        lineas.map((l) => (
          <Typography key={l} sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>
            {l}
          </Typography>
        ))
      ) : (
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
      )}
      {nota ? (
        <Typography sx={{ fontSize: "0.66rem", color: FP.color.textMuted }}>{nota}</Typography>
      ) : null}
    </Box>
  );
}

/**
 * ¿Esta factura se emitió en una moneda distinta a la base del tenant?
 *
 * El backend manda todos los importes convertidos a la base (así se pueden
 * sumar) y además los mismos importes en la moneda del documento (`*mon`).
 * Una factura en dólares se muestra en dólares: es lo que el cliente debe y
 * lo que va a pagar; el equivalente convertido va al lado, entre paréntesis.
 */
const esOtraMoneda = (f, base) => normalizaLegacy(f?.moneda) !== normalizaLegacy(base);

/** Importe de una factura con su símbolo: el nativo si no es la base. */
const montoFactura = (f, base, mon, conv) =>
  esOtraMoneda(f, base)
    ? `${monedaLabel(f.moneda)} ${fmtMoney(mon)}`
    : `${monedaLabel(base)} ${fmtMoney(conv)}`;

/** Equivalente en la moneda base, solo cuando la factura no está en ella. */
const equivalenteBase = (f, base, conv) =>
  esOtraMoneda(f, base) ? ` (${monedaLabel(base)} ${fmtMoney(conv)})` : "";

/** Importe de una cuota, en la misma moneda que su factura. */
const montoCuota = (f, base, c, campo) =>
  fmtMoney(esOtraMoneda(f, base) ? c[`${campo}mon`] : c[campo]);

/** Sufijo de las cabeceras de cuotas cuando la factura no está en la base. */
const sufijoMoneda = (f, base) => (esOtraMoneda(f, base) ? ` ${monedaLabel(f.moneda)}` : "");

// Estado de cuenta completo (compañía + facturas con sus cuotas).
// `pormoneda` es el saldo agrupado por la moneda en que se emitió cada
// factura: el resto del documento va convertido a la base, y con facturas en
// dólares eso no dice cuánto hay que cobrar en cada moneda.
function imprimirEstado(compania, cliente, fechacorte, facturas, totales, base, pormoneda = []) {
  // Con facturas fuera de la moneda base, los totales del pie se imprimen
  // por moneda emitida (que es lo que hay que cobrar) y el convertido queda
  // como referencia al final.
  const mezcla = facturas.some((f) => esOtraMoneda(f, base));
  const totalesPorMoneda = mezcla
    ? (pormoneda || [])
        .map(
          (m) => `<p style="text-align:right;font-weight:bold;margin:0 0 2px">
        ${monedaLabel(m.moneda)} — Facturado: ${fmtMoney(m.valor)} ·
        Pagado: ${fmtMoney(m.pagado)} ·${
            parseFloat(m.avance || 0) ? ` Avance: ${fmtMoney(m.avance)} ·` : ""
          }
        Balance: ${fmtMoney(m.balance)}
      </p>
      <p style="text-align:right;font-size:11px;margin:0 0 8px">
        Días vencidos — 0-30: ${fmtMoney(m.t0a30)} · 31-60: ${fmtMoney(m.t31a60)} ·
        61-90: ${fmtMoney(m.t61a90)} · +90: ${fmtMoney(m.tmas90)}${
            parseFloat(m.credito || 0) ? ` · A favor: ${fmtMoney(m.credito)}` : ""
          }
      </p>`
        )
        .join("")
    : "";
  const bloques = facturas
    .map((f) => {
      const cuotas = (f.cuotas || [])
        .map(
          (c) => `<tr>
            <td style="padding:3px 8px;border-bottom:1px solid #ddd">${etiquetaCuota(
              c.numero_pago
            )}</td>
            <td style="padding:3px 8px;border-bottom:1px solid #ddd">${c.fecha}</td>
            <td style="padding:3px 8px;border-bottom:1px solid #ddd;text-align:right">${montoCuota(
              f,
              base,
              c,
              "valor"
            )}</td>
            <td style="padding:3px 8px;border-bottom:1px solid #ddd;text-align:right">${montoCuota(
              f,
              base,
              c,
              "valor_pagado"
            )}</td>
            <td style="padding:3px 8px;border-bottom:1px solid #ddd;text-align:right">${montoCuota(
              f,
              base,
              c,
              "pendiente"
            )}</td>
          </tr>`
        )
        .join("");
      const avance = parseFloat(f.vavance || 0)
        ? ` · Avance: ${montoFactura(f, base, f.vavancemon, f.vavance)}`
        : "";
      // La factura se imprime en su moneda; el convertido queda al lado del
      // balance para poder cuadrarla contra los totales del reporte.
      const enMoneda = equivalenteBase(f, base, f.balance);
      const suf = sufijoMoneda(f, base);
      return `
        <div style="margin-bottom:16px">
          <p style="margin:0 0 4px;font-weight:bold">Factura #${f.id} · ${f.fecha} · Días: ${
        f.dias
      }${f.atraso > 0 ? ` · Atraso: ${f.atraso} días` : " · Al día"}</p>
          <p style="margin:0 0 6px;font-size:11px">Valor: ${montoFactura(
            f,
            base,
            f.valormon,
            f.valor
          )} · Aplicado: ${montoFactura(
        f,
        base,
        f.vplicadomon,
        f.vplicado
      )}${avance} · Balance: ${montoFactura(f, base, f.balancemon, f.balance)}${enMoneda}</p>
          <table style="width:100%;border-collapse:collapse">
            <thead><tr style="background:#eee">
              <th style="padding:4px 8px;text-align:left">Cuota</th>
              <th style="padding:4px 8px;text-align:left">Fecha</th>
              <th style="padding:4px 8px;text-align:right">Valor${suf}</th>
              <th style="padding:4px 8px;text-align:right">Pagado${suf}</th>
              <th style="padding:4px 8px;text-align:right">Pendiente${suf}</th>
            </tr></thead>
            <tbody>${cuotas}</tbody>
          </table>
        </div>`;
    })
    .join("");
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Estado de Cuenta Cliente</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:12px">
      <h3 style="text-align:center;margin:0 0 2px">${compania?.nombre || ""}</h3>
      <p style="text-align:center;margin:0">${compania?.direccion || ""}</p>
      <p style="text-align:center;margin:0 0 10px">${
        compania?.rnc ? `RNC: ${compania.rnc}` : ""
      }</p>
      <p style="text-align:center;margin:0 0 2px;font-weight:bold;font-size:14px">Estado de Cuenta Cliente</p>
      <p style="text-align:center;margin:0 0 14px;font-size:12px;color:#555">${
        cliente?.descrip || ""
      }${cliente?.rnc ? ` · RNC: ${cliente.rnc}` : ""} · Corte al ${fechaDMY(fechacorte)}</p>
      ${bloques}
      <div style="border-top:1px solid #000;padding-top:6px">${totalesPorMoneda}</div>
      <p style="text-align:right;font-weight:bold;${
        mezcla ? "font-size:11px;color:#555" : "border-top:1px solid #000;padding-top:6px"
      }">
        ${mezcla ? `Equivalente en ${monedaLabel(base)} — ` : ""}Facturado: ${monedaLabel(
    base
  )} ${fmtMoney(totales?.valor)} ·
        Pagado: ${monedaLabel(base)} ${fmtMoney(totales?.pagado)} ·${
    parseFloat(totales?.avance || 0)
      ? ` Avance: ${monedaLabel(base)} ${fmtMoney(totales?.avance)} ·`
      : ""
  }
        Balance: ${monedaLabel(base)} ${fmtMoney(totales?.balance)}
      </p>
      <p style="text-align:right;font-size:11px;${mezcla ? "color:#555" : ""}">
        Días vencidos —
        0-30: ${monedaLabel(base)} ${fmtMoney(totales?.t0a30)} ·
        31-60: ${monedaLabel(base)} ${fmtMoney(totales?.t31a60)} ·
        61-90: ${monedaLabel(base)} ${fmtMoney(totales?.t61a90)} ·
        +90: ${monedaLabel(base)} ${fmtMoney(totales?.tmas90)}${
    parseFloat(totales?.credito || 0)
      ? ` · A favor: ${monedaLabel(base)} ${fmtMoney(totales?.credito)}`
      : ""
  }
      </p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

const cellSx = { fontSize: "0.8rem", py: 0.6 };
const cellRightSx = { ...cellSx, textAlign: "right" };
const headSx = { fontSize: "0.72rem", fontWeight: 800, color: FP.color.textMuted, py: 0.6 };

export default function EstadoCuentaXmayor() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [fechacorte, setFechacorte] = useState(hoyISO());
  const [config, setConfig] = useState(null);

  const [facturas, setFacturas] = useState([]);
  const [totales, setTotales] = useState(null);
  // Saldo agrupado por la moneda de emisión de cada factura: lo que el
  // cliente debe en pesos y lo que debe en dólares, sin convertir.
  const [pormoneda, setPormoneda] = useState([]);
  // Moneda en la que el backend ya expresó todos los importes.
  const [base, setBase] = useState("DOP");
  const [procesado, setProcesado] = useState(null); // cliente del último proceso
  // ¿Hay facturas fuera de la moneda base? Entonces los totales convertidos
  // no son lo que el cliente debe: cada uno se muestra por moneda emitida y
  // el convertido baja a nota de referencia.
  const mezcla = hayOtraMoneda(facturas, base);
  const lineasMoneda = (campo) =>
    mezcla ? pormoneda.map((m) => `${monedaLabel(m.moneda)} ${fmtMoney(m[campo])}`) : null;
  const notaEquivalente = (v) => (mezcla ? `≈ ${monedaLabel(base)} ${fmtMoney(v)}` : null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    post("/api/xmayor_clientes", {}).then(({ ok, data }) => {
      if (ok) setClientes(data.clientes || []);
    });
    post("/api/xmayor_config", {}).then(({ ok, data }) => {
      if (ok) setConfig(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    if (!cliente) {
      showAlert({ message: "Seleccione el cliente", color: "warning" });
      return;
    }
    setLoading(true);
    try {
      const { ok, data } = await post("/api/xmayor_estadocuenta", {
        cliente: cliente.id,
        fechacorte,
      });
      if (ok) {
        setFacturas(data.facturas || []);
        setTotales(data.totales || null);
        setPormoneda(data.pormoneda || []);
        setBase(data.base || "DOP");
        setProcesado(cliente);
      } else {
        showAlert({
          message: data.Error || "No se pudo generar el estado de cuenta",
          color: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const subtitulo = `${procesado?.descrip || ""} · Corte al ${fechaDMY(fechacorte)}`;

  const exportar = () => {
    const filas = [
      ["Estado de Cuenta Cliente x Mayor"],
      [subtitulo],
      [],
      ["Fact#", "Fecha", "Moneda", "Valor", "Aplicado", "Balance", "Días", "Atraso"],
    ];
    // Cada factura va en su moneda (columna Moneda); el TOTAL es el
    // equivalente en la base, que es la única forma de sumarlas juntas.
    facturas.forEach((f) => {
      const otra = esOtraMoneda(f, base);
      filas.push([
        f.id,
        f.fecha,
        monedaLabel(otra ? f.moneda : base),
        otra ? f.valormon : f.valor,
        otra ? f.vplicadomon : f.vplicado,
        otra ? f.balancemon : f.balance,
        f.dias,
        f.atraso,
      ]);
      filas.push(["", "Cuota", "Fecha", "Valor", "Pagado", "Pendiente"]);
      (f.cuotas || []).forEach((c) => {
        filas.push([
          "",
          etiquetaCuota(c.numero_pago),
          c.fecha,
          otra ? c.valormon : c.valor,
          otra ? c.valor_pagadomon : c.valor_pagado,
          otra ? c.pendientemon : c.pendiente,
        ]);
      });
      filas.push([]);
    });
    filas.push([
      "",
      `TOTAL (equivalente en ${monedaLabel(base)})`,
      "",
      totales?.valor || 0,
      totales?.pagado || 0,
      totales?.balance || 0,
      "",
    ]);
    filas.push([]);
    if (mezcla) {
      filas.push(["Saldo por moneda de emisión"]);
      filas.push(pormoneda.map((m) => monedaLabel(m.moneda)));
      filas.push(pormoneda.map((m) => m.balance));
      filas.push([]);
    }
    filas.push(["Antigüedad por días vencidos de cuota"]);
    filas.push(["0-30", "31-60", "61-90", "+90", "A favor"]);
    filas.push([
      totales?.t0a30 || 0,
      totales?.t31a60 || 0,
      totales?.t61a90 || 0,
      totales?.tmas90 || 0,
      totales?.credito || 0,
    ]);
    const hoja = XLSX.utils.aoa_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Estado de Cuenta");
    XLSX.writeFile(libro, `EstadoCuentaXmayor_${hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    imprimirEstado(config?.compania, procesado, fechacorte, facturas, totales, base, pormoneda);
  };

  const hayDatos = facturas.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Estado de Cuenta Cliente"
        subtitle="Facturas x mayor pendientes del cliente con antigüedad por vencimiento de cuota y detalle de cuotas"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={5} md={4}>
              <Autocomplete
                options={clientes}
                value={cliente}
                onChange={(_, v) => setCliente(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Cliente" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de corte"
                InputLabelProps={{ shrink: true }}
                value={fechacorte}
                onChange={(e) => setFechacorte(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip
              label="FACTURADO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.valor)}`}
              lineas={lineasMoneda("valor")}
              nota={notaEquivalente(totales?.valor)}
            />
            <ResumenChip
              label="PAGADO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.pagado)}`}
              lineas={lineasMoneda("pagado")}
              nota={notaEquivalente(totales?.pagado)}
              color={FP.color.success}
            />
            {parseFloat(totales?.avance || 0) > 0 && (
              <ResumenChip
                label="AVANCE"
                value={`${monedaLabel(base)} ${fmtMoney(totales?.avance)}`}
                lineas={lineasMoneda("avance")}
                nota={notaEquivalente(totales?.avance)}
                color={FP.color.success}
              />
            )}
            <ResumenChip
              label="BALANCE"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.balance)}`}
              lineas={lineasMoneda("balance")}
              nota={notaEquivalente(totales?.balance)}
            />
            <ResumenChip
              label="0-30 VENCIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.t0a30)}`}
              lineas={lineasMoneda("t0a30")}
              nota={notaEquivalente(totales?.t0a30)}
              color="#2E7D32"
            />
            <ResumenChip
              label="31-60 VENCIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.t31a60)}`}
              lineas={lineasMoneda("t31a60")}
              nota={notaEquivalente(totales?.t31a60)}
              color="#F9A825"
            />
            <ResumenChip
              label="61-90 VENCIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.t61a90)}`}
              lineas={lineasMoneda("t61a90")}
              nota={notaEquivalente(totales?.t61a90)}
              color="#EF6C00"
            />
            <ResumenChip
              label="+90 VENCIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.tmas90)}`}
              lineas={lineasMoneda("tmas90")}
              nota={notaEquivalente(totales?.tmas90)}
              color="#C62828"
            />
            {parseFloat(totales?.credito || 0) > 0 && (
              <ResumenChip
                label="A FAVOR"
                value={`${monedaLabel(base)} ${fmtMoney(totales?.credito)}`}
                lineas={lineasMoneda("credito")}
                nota={notaEquivalente(totales?.credito)}
                color={FP.color.success}
              />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          {!hayDatos && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 8, color: FP.color.textMuted, bgcolor: "#fff", borderRadius: 2 }}
            >
              <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
              <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
                Sin datos — seleccione el cliente y procese
              </Typography>
            </Stack>
          )}

          {facturas.map((f) => (
            <Box
              key={f.id}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                bgcolor: "#fff",
                p: { xs: 1.5, sm: 2 },
                mb: 1.6,
              }}
            >
              <Stack
                direction="row"
                spacing={1.2}
                flexWrap="wrap"
                alignItems="center"
                sx={{ mb: 1 }}
                useFlexGap
              >
                <Typography sx={{ fontWeight: 800, color: FP.color.navy }}>
                  Factura #{f.id} · {f.fecha}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted }}>
                  Días: {f.dias}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: f.atraso > 0 ? "#C62828" : FP.color.success,
                  }}
                >
                  {f.atraso > 0 ? `Atraso: ${f.atraso} días` : "Al día"}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                  {`Valor: ${montoFactura(f, base, f.valormon, f.valor)}`}
                  {` · Aplicado: ${montoFactura(f, base, f.vplicadomon, f.vplicado)}`}
                  {parseFloat(f.vavance || 0) > 0
                    ? ` · Avance: ${montoFactura(f, base, f.vavancemon, f.vavance)}`
                    : ""}
                  {` · Balance: ${montoFactura(f, base, f.balancemon, f.balance)}`}
                  {/* el convertido, para poder cuadrar contra los totales */}
                  <Box component="span" sx={{ fontWeight: 500, color: FP.color.textSecondary }}>
                    {equivalenteBase(f, base, f.balance)}
                  </Box>
                </Typography>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headSx}>CUOTA</TableCell>
                    <TableCell sx={headSx}>FECHA</TableCell>
                    <TableCell sx={{ ...headSx, textAlign: "right" }}>
                      {`VALOR${sufijoMoneda(f, base)}`}
                    </TableCell>
                    <TableCell sx={{ ...headSx, textAlign: "right" }}>
                      {`PAGADO${sufijoMoneda(f, base)}`}
                    </TableCell>
                    <TableCell sx={{ ...headSx, textAlign: "right" }}>
                      {`PENDIENTE${sufijoMoneda(f, base)}`}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(f.cuotas || []).map((c) => (
                    <TableRow key={c.numero_pago}>
                      <TableCell sx={cellSx}>{etiquetaCuota(c.numero_pago)}</TableCell>
                      <TableCell sx={cellSx}>{c.fecha}</TableCell>
                      <TableCell sx={cellRightSx}>{montoCuota(f, base, c, "valor")}</TableCell>
                      <TableCell sx={cellRightSx}>
                        {montoCuota(f, base, c, "valor_pagado")}
                      </TableCell>
                      <TableCell sx={cellRightSx}>{montoCuota(f, base, c, "pendiente")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
